import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { convertColorToThemeHexCode } from 'Styles/colors.util';
import * as Styled from './TextCard.styled';
import CtaPropType from '../../prop-types/cta';
import RichTextPropTypes from '../../prop-types/rich-text';
import RichText from '../RichText/RichText';
import Reveal from '../Reveal/Reveal';
import ImageProps from '../../prop-types/image-props.pim';
import { TextCardFragment } from './TextCard.gql';

/**
 * A card that can render a header, rich text, and CTAs
 */
const TextCard = ({
  alignment,
  backgroundColor,
  customBackgroundColor,
  icon,
  cta: ctas,
  gridSpan,
  header,
  subheader,
  responsiveStyleSize,
  className,
  containerBackgroundColor
}) => {
  const [areCtasRevealed, setAreCtasRevealed] = useState(false);
  const isTransparent = backgroundColor === 'transparent';
  const backgroundHexcode =
    customBackgroundColor ||
    (!isTransparent && convertColorToThemeHexCode(backgroundColor)) ||
    containerBackgroundColor;
  const hasCtas = ctas?.length || null;
  const hasOneCta = hasCtas && ctas.length === 1;
  const hasMoreThanOneCta = hasCtas && ctas.length > 1;

  /**
   * If there is more than one CTA, force Reveal module
   */
  const href = hasOneCta && ctas[0].url;

  return (
    <Styled.TextCard
      $backgroundColor={backgroundColor}
      $gridSpan={gridSpan}
      $areCtasRevealed={areCtasRevealed}
      $isClickable={hasCtas}
      $alignment={alignment}
      onClick={() => hasMoreThanOneCta && setAreCtasRevealed(true)}
      as={href && 'a'}
      className={className}
      {...(href ? { href } : {})}
    >
      <Styled.Copy
        $backgroundColor={backgroundHexcode}
        $alignment={alignment}
        $gridSpan={gridSpan}
        $withPadding={!isTransparent}
        $responsiveStyleSize={responsiveStyleSize}
        $areCtasRevealed={areCtasRevealed}
      >
        {icon && icon?.file?.url && (
          <Styled.Icon
            src={icon.file.url}
            alt={icon.description}
            $alignment={alignment}
          />
        )}
        {!!header && (
          <Styled.Title
            $backgroundColor={backgroundHexcode}
            $responsiveStyleSize={responsiveStyleSize}
          >
            {header}
          </Styled.Title>
        )}
        {!!subheader && !!Object.keys(subheader).length && (
          <Styled.Subheader $responsiveStyleSize={responsiveStyleSize}>
            <RichText richText={subheader} />
          </Styled.Subheader>
        )}
        {hasOneCta && !!(ctas[0].text && ctas[0].text.trim()) && (
          <Styled.Cta
            as="div"
            size={responsiveStyleSize === 'mega' ? 'medium' : 'small'}
            variant="defaultDark"
            $backgroundColor={backgroundHexcode}
          >
            {ctas[0].text}
          </Styled.Cta>
        )}
        {hasMoreThanOneCta && ctas[0].text && ctas[0].text.trim() && (
          <Styled.Cta
            $backgroundColor={backgroundHexcode}
            key={header + ctas[0].text + ctas[0].url}
            onClick={() => setAreCtasRevealed(true)}
            role="button"
            size={responsiveStyleSize === 'mega' ? 'medium' : 'small'}
            variant="defaultDark"
          >
            {ctas[0].text}
          </Styled.Cta>
        )}
      </Styled.Copy>
      {hasMoreThanOneCta && areCtasRevealed && (
        <Reveal
          setIsOpen={setAreCtasRevealed}
          ctas={ctas}
          backgroundColor={backgroundHexcode}
          size={responsiveStyleSize}
        />
      )}
    </Styled.TextCard>
  );
};

TextCard.fragment = TextCardFragment;

TextCard.propTypes = {
  /**
   * Alignment of text
   */
  alignment: PropTypes.oneOf(['left', 'center']),
  /**
   * Background color
   */
  backgroundColor: PropTypes.oneOf([
    'black',
    'blue',
    'blue20',
    'grey20',
    'grey40',
    'grey60',
    'grey80',
    'green',
    'red',
    'white',
    'transparent'
  ]),
  /**
   * A hex code. If customBackgroundColor is used, it overrides background color
   */
  customBackgroundColor: PropTypes.string,
  /**
   * A small decorative image that displays above the copy
   */
  icon: ImageProps,
  /**
   * Links to display
   */
  cta: PropTypes.arrayOf(PropTypes.shape(CtaPropType)),
  /**
   * Column spans in a 12 column grid
   *
   * Accepts a number or a string
   */
  gridSpan: ({ gridSpan }, propName, componentName) =>
    gridSpan && gridSpan >= 1 && gridSpan <= 12
      ? null
      : new Error(`${propName} in ${componentName} is not between 1 and 12`),
  /**
   * Title to display
   */
  header: PropTypes.string,
  /**
   * The supporting text for the header.
   *
   * Renders as contentful rich text
   */
  subheader: PropTypes.shape(RichTextPropTypes),
  /**
   * Applies styling to copy based on content system responsive rules
   */
  responsiveStyleSize: PropTypes.oneOf(['mini', 'regular', 'mega']),
  /**
   * A CSS classname
   */
  className: PropTypes.string,
  /**
   * Background color of the parent container
   */
  containerBackgroundColor: PropTypes.string
};

TextCard.defaultProps = {
  alignment: 'left',
  backgroundColor: 'white',
  customBackgroundColor: null,
  icon: {},
  cta: [],
  gridSpan: 6,
  header: '',
  subheader: {},
  responsiveStyleSize: 'regular',
  className: null,
  containerBackgroundColor: convertColorToThemeHexCode('white')
};

export default TextCard;
